import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Key, Eye, Zap, ShieldCheck, CheckCircle, XCircle, Clock } from "lucide-react";

const VIEW_PERMS = [
  { key: "view_users", label: "View Users" },
  { key: "view_students", label: "View Students" },
  { key: "view_modules", label: "View Modules" },
  { key: "view_courses", label: "View Courses" },
  { key: "view_groups", label: "View Groups" },
  { key: "view_payments", label: "View Payments" },
  { key: "view_invoices", label: "View Invoices" },
  { key: "view_sessions", label: "View Sessions" },
  { key: "view_inventory", label: "View Inventory" },
];

const ACTION_PERMS = [
  { key: "manage_modules", label: "Create/Edit Modules" },
  { key: "assign_students", label: "Assign Students" },
  { key: "manage_groups", label: "Manage Groups" },
  { key: "manage_courses", label: "Manage Courses" },
  { key: "manage_payments", label: "Create/Edit Payments" },
  { key: "generate_invoices", label: "Generate Invoices" },
  { key: "manage_users", label: "Manage Users" },
  { key: "post_feed", label: "Post on Studio Feed" },
  { key: "comment_feed", label: "Comment/Reply on Posts" },
  { key: "manage_inventory", label: "Manage Inventory" },
];

function PermToggle({ label, checked, onChange, approvalChecked, onApprovalChange, showApproval }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#F7F3EF] transition-colors">
      <label className="flex items-center gap-3 cursor-pointer flex-1">
        <input type="checkbox" checked={checked} onChange={onChange} className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969] w-4 h-4" />
        <span className="text-sm text-[#2B2B2B]">{label}</span>
      </label>
      {showApproval && checked && (
        <label className="flex items-center gap-1.5 cursor-pointer ml-4" title="Requires admin approval">
          <input type="checkbox" checked={approvalChecked} onChange={onApprovalChange} className="rounded border-[#C6A969] text-[#C6A969] focus:ring-[#C6A969] w-3.5 h-3.5" />
          <span className="text-[10px] text-[#C6A969] font-medium whitespace-nowrap">Needs Approval</span>
        </label>
      )}
    </div>
  );
}

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [editRole, setEditRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", permissions: [], approval_required: [] });

  const load = useCallback(async () => {
    const [r, a] = await Promise.all([
      api.get("/custom-roles").catch(() => ({ data: [] })),
      api.get("/approvals").catch(() => ({ data: [] })),
    ]);
    setRoles(r.data);
    setApprovals(a.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditRole(null);
    setForm({ name: "", permissions: [], approval_required: [] });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditRole(role);
    setForm({ name: role.name, permissions: [...(role.permissions || [])], approval_required: [...(role.approval_required || [])] });
    setDialogOpen(true);
  };

  const togglePerm = (key) => {
    setForm((f) => {
      const perms = f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key];
      const approval = f.permissions.includes(key) ? f.approval_required.filter((p) => p !== key) : f.approval_required;
      return { ...f, permissions: perms, approval_required: approval };
    });
  };

  const toggleApproval = (key) => {
    setForm((f) => ({
      ...f,
      approval_required: f.approval_required.includes(key)
        ? f.approval_required.filter((p) => p !== key)
        : [...f.approval_required, key]
    }));
  };

  const selectAll = () => {
    setForm((f) => ({ ...f, permissions: [...VIEW_PERMS.map((p) => p.key), ...ACTION_PERMS.map((p) => p.key)] }));
  };

  const clearAll = () => {
    setForm((f) => ({ ...f, permissions: [], approval_required: [] }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Role name is required"); return; }
    try {
      if (editRole) {
        await api.put(`/custom-roles/${editRole.id}`, form);
        toast.success("Role updated");
      } else {
        await api.post("/custom-roles", form);
        toast.success("Role created");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete "${role.name}"? Users with this role will lose their permissions.`)) return;
    try { await api.delete(`/custom-roles/${role.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleApproval = async (id, action) => {
    try {
      await api.post(`/approvals/${id}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pendingApprovals = approvals.filter((a) => a.status === "pending");

  return (
    <AdminLayout>
      <div data-testid="admin-roles" className="space-y-8 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Roles & Permissions</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">Create custom roles with configurable access</p>
          </div>
          <Button data-testid="create-role-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Plus className="w-4 h-4 mr-1.5" /> New Role
          </Button>
        </div>

        {/* Built-in Roles Info */}
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-3">Built-in Roles</p>
          <div className="flex flex-wrap gap-2">
            {["Admin (full access)", "Instructor", "Student", "Guardian", "Spectator"].map((r) => (
              <span key={r} className="px-3 py-1.5 bg-[#F7F3EF] rounded-full text-xs font-medium text-[#5C3A2E]">{r}</span>
            ))}
          </div>
        </div>

        {/* Custom Roles */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-3">Custom Roles</p>
          {roles.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#EADBC8]/30 p-8 text-center">
              <Key className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">No custom roles yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {roles.map((role) => (
                <div key={role.id} data-testid={`role-card-${role.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 studio-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{role.name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#2B2B2B]/40">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {(role.permissions || []).filter((p) => p.startsWith("view_")).length} view</span>
                        <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {(role.permissions || []).filter((p) => !p.startsWith("view_")).length} action</span>
                        {(role.approval_required || []).length > 0 && (
                          <span className="flex items-center gap-1 text-[#C6A969]"><ShieldCheck className="w-3 h-3" /> {role.approval_required.length} need approval</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button data-testid={`edit-role-${role.id}`} variant="ghost" size="sm" onClick={() => openEdit(role)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-role-${role.id}`} variant="ghost" size="sm" onClick={() => handleDelete(role)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Approval Queue */}
        {pendingApprovals.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-3">Pending Approvals</p>
            <div className="grid gap-3">
              {pendingApprovals.map((a) => (
                <div key={a.id} data-testid={`approval-${a.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#C6A969]/30 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#C6A969]" />
                        <span className="text-sm font-medium text-[#2B2B2B]">{a.description || a.action}</span>
                      </div>
                      <p className="text-xs text-[#2B2B2B]/40 mt-1">By {a.user_name} · {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => handleApproval(a.id, "approve")} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-8 px-3 gap-1">
                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleApproval(a.id, "reject")} className="text-red-400 hover:bg-red-50 rounded-full h-8 px-3 gap-1">
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">{editRole ? "Edit Role" : "New Custom Role"}</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Configure permissions for this role</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Role Name</label>
                <Input data-testid="role-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Kalemash Support" className="rounded-xl border-[#EADBC8]" />
              </div>

              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs text-[#C6A969] hover:underline">Select All</button>
                <span className="text-[#EADBC8]">|</span>
                <button onClick={clearAll} className="text-xs text-[#2B2B2B]/40 hover:underline">Clear All</button>
              </div>

              {/* View Permissions */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-[#5C3A2E]" />
                  <span className="text-sm font-medium text-[#5C3A2E]">View Permissions</span>
                </div>
                <div className="border border-[#EADBC8]/50 rounded-xl divide-y divide-[#EADBC8]/20">
                  {VIEW_PERMS.map((p) => (
                    <PermToggle
                      key={p.key}
                      label={p.label}
                      checked={form.permissions.includes(p.key)}
                      onChange={() => togglePerm(p.key)}
                      showApproval={false}
                    />
                  ))}
                </div>
              </div>

              {/* Action Permissions */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-[#C6A969]" />
                  <span className="text-sm font-medium text-[#5C3A2E]">Action Permissions</span>
                </div>
                <div className="border border-[#EADBC8]/50 rounded-xl divide-y divide-[#EADBC8]/20">
                  {ACTION_PERMS.map((p) => (
                    <PermToggle
                      key={p.key}
                      label={p.label}
                      checked={form.permissions.includes(p.key)}
                      onChange={() => togglePerm(p.key)}
                      approvalChecked={form.approval_required.includes(p.key)}
                      onApprovalChange={() => toggleApproval(p.key)}
                      showApproval={true}
                    />
                  ))}
                </div>
              </div>

              <Button data-testid="save-role-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                {editRole ? "Save Changes" : "Create Role"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
